import { getApiBase } from "./apiBase";
import { dropSession, isAuthError } from "./session";

/**
 * Профиль участника с платформы: достижения и балансы.
 *
 * Ручка /v1/profile/index уже существует — её делали для FairyTON, и она
 * отдаёт ровно то, что нужно кабинету: уровень, число локаций, пройденные
 * квесты, друзей и балансы в трёх валютах. Своей ручки заводить не стали
 * намеренно: два источника одних и тех же цифр рано или поздно разойдутся,
 * и человек увидит в приложении одно, а в кабинете другое.
 *
 * Ответ платформы:
 *   user   — level, my_locations, quest_completed, friends, name_first,
 *            name_last, country, town, photo, telegram_username
 *   wallet — fd_point, tokens, nft, fd_usdt
 *   passive_income — fdp, fdt, fdusd
 */
export const fetchProfile = async () => {
  const base = getApiBase();
  if (!base) {
    throw new Error("не задан адрес API");
  }

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${base}/v1/profile/index`, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });

  const data = await res.json().catch(() => ({}));

  if (isAuthError(res.status, data && data.message)) {
    dropSession();
    const error = new Error("сессия истекла, войдите заново");
    error.status = res.status;
    throw error;
  }

  if (!res.ok) {
    throw new Error(data && data.message ? data.message : `код ${res.status}`);
  }

  return data;
};

/**
 * Фото профиля — или null, если платформа отдала свою заглушку.
 *
 * UserAvatar::getAvatar() никогда не возвращает пусто: когда своей картинки
 * у человека нет, он отдаёт /images/iam.png из ассетов старой админки —
 * серый силуэт. Для кабинета это то же самое, что «фото нет», но отличить
 * одно от другого можно только по имени файла: путь с хешем публикации
 * (assets/4156d26e/…) меняется при каждой перепубликации ассетов, а хвост
 * остаётся. Так же эту заглушку узнаёт и сама админка — сравнением
 * с '/images/iam.png' в frontend/services/Html.php.
 *
 * Без этой проверки ветка с инициалами в шапке недостижима: photo всегда
 * непустой, и у всех, кто не загружал фото, висит чужой серый силуэт.
 *
 * @param {string|null} photo значение поля user.photo из /v1/profile/index
 *
 * @returns {string|null} null, если это заглушка или ничего не пришло
 */
/**
 * Сохранение анкеты.
 *
 * Ручка /v1/profile/edit существует давно — её использует старая админка
 * через ту же форму Profile2. Кабинет ею не пользовался, и в профиле стояла
 * оговорка «подключим, когда появится ручка»: она появилась раньше, чем
 * оговорку успели снять.
 *
 * Что принимает: user — name_first, name_last, name_middle, country, town,
 * gender, avatar; socials — vk, fb, twitter, telegram, whatsapp, instagram,
 * youtube, phone, email, viber. Оба блока необязательны, шлём только то,
 * что человек мог изменить.
 *
 * Почту не отправляем сознательно: по ней приходят награды и к ней привязаны
 * покупки, и менять её через анкету нельзя — на платформе это отдельная
 * процедура со сверкой.
 */
export const saveProfile = async ({ user, socials }) => {
  const base = getApiBase();
  if (!base) {
    throw new Error("не задан адрес API");
  }

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const body = {};
  if (user) body.user = user;
  if (socials) body.socials = socials;

  const res = await fetch(`${base}/v1/profile/edit`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));

  if (isAuthError(res.status, data && data.message)) {
    dropSession();
    const error = new Error("сессия истекла, войдите заново");
    error.status = res.status;
    throw error;
  }

  /* Ошибки валидации приходят объектом {поле: [сообщения]} — разворачиваем
     в строку, иначе на экран попадёт «[object Object]». */
  if (!res.ok) {
    const m = data && data.message;
    if (m && typeof m === "object") {
      const first = Object.keys(m).map((k) => [].concat(m[k]).join(", ")).join("; ");
      throw new Error(first || `код ${res.status}`);
    }
    throw new Error(m || `код ${res.status}`);
  }

  return data;
};

export const avatarOrNull = (photo) => {
  if (typeof photo !== "string" || photo.trim() === "") return null;

  return photo.endsWith("/images/iam.png") ? null : photo;
};

/**
 * Достижения для шапки профиля.
 *
 * Возвращает null там, где платформа ничего не прислала: прочерк честнее
 * нуля. Ноль читается как «у вас ничего нет», хотя на деле мы просто не
 * получили число.
 */
export const readAchievements = (profile) => {
  const u = (profile && profile.user) || {};
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

  return {
    level: num(u.level),
    locations: num(u.my_locations),
    quests: num(u.quest_completed),
    friends: num(u.friends),
  };
};

/** Балансы кошелька в том порядке, в каком они показаны в разделе. */
export const readWallet = (profile) => {
  const w = (profile && profile.wallet) || {};
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

  return {
    point: num(w.fd_point),
    token: num(w.tokens),
    usdt: num(w.fd_usdt),
    // Рублёвого баланса ручка не отдаёт: на платформе он есть отдельной
    // валютой (id 7), но в профиль FairyTON не попал. Пока прочерк.
    rub: null,
  };
};
